import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from "react-redux";

import 'intersection-observer';
import { ProductListImage } from 'molo-shared/lib/components/ProductListImage';
import { isBrandedRevealImageCached } from 'molo-shared/lib/components/BrandedRevealImage';

// Store.
import { RootState } from '../../store/';

// Models.
import { StyleBean } from '../../models/Beans'

// utils.
import { roundAmount, getWHPWithDiscount } from '../../utils/priceHelper';
import {
    buildProductAlternateImageUrl,
    buildProductImageUrlCandidates,
    ProductImageProfile,
} from '../../utils/productImageUrl';

// Icons.
import { FaComment, FaHeart, FaRegHeart } from 'react-icons/fa';

const ProductListItem: React.FC<
    {
        product:StyleBean,
        discount?: number,
        inBasket?: boolean,
        lazyLoadMoloMaster?: boolean
        quantityOveride?: number
        quantityLabel?: string
        scrollPosition?: any
        hidePrice?: boolean
        wishlistOn?: boolean
        wishlistLoading?: boolean
        onWishlistClick?: (e: React.MouseEvent) => void
        imageProfile?: ProductImageProfile
        otherOpenOrderLabel?: string
        otherOpenOrderTitle?: string
    }> = (props) => {
    const { product, hidePrice, imageProfile = 'thumb' } = props;

    const user = useSelector((state: RootState) => state.user.user);
    const { seasonCode, masterId, colorCode } = product;
    const imageCandidates = useMemo(
        () => buildProductImageUrlCandidates(seasonCode, masterId, colorCode, imageProfile),
        [seasonCode, masterId, colorCode, imageProfile]
    );
    const imageUrl = imageCandidates[0];
    const imageFallbackSrcs = useMemo(() => imageCandidates.slice(1), [imageCandidates]);
    const hoverImageUrl = useMemo(
        () => buildProductAlternateImageUrl(seasonCode, masterId, colorCode, imageProfile),
        [seasonCode, masterId, colorCode, imageProfile]
    );
    const [imageReady, setImageReady] = useState(() => isBrandedRevealImageCached(imageUrl));
    const [stickerVisible, setStickerVisible] = useState(false);
    const [otherOrderStickerVisible, setOtherOrderStickerVisible] = useState(false);
    const [hoverImageAvailable, setHoverImageAvailable] = useState(false);
    const showOtherOpenOrder =
        !props.inBasket && props.otherOpenOrderLabel && props.otherOpenOrderLabel.length > 0;

    useEffect(() => {
        const cached = isBrandedRevealImageCached(imageUrl);
        setImageReady(cached);
        if (!cached) {
            setStickerVisible(false);
        }
    }, [imageUrl]);

    useEffect(() => {
        let cancelled = false;
        setHoverImageAvailable(false);

        const img = new Image();
        img.onload = () => {
            if (!cancelled) {
                setHoverImageAvailable(true);
            }
        };
        img.onerror = () => {
            if (!cancelled) {
                setHoverImageAvailable(false);
            }
        };
        img.src = hoverImageUrl;

        return () => {
            cancelled = true;
        };
    }, [hoverImageUrl]);

    useEffect(() => {
        if (!props.inBasket || !imageReady) {
            setStickerVisible(false);
            return;
        }

        const frame = requestAnimationFrame(() => setStickerVisible(true));
        return () => cancelAnimationFrame(frame);
    }, [props.inBasket, imageReady]);

    useEffect(() => {
        if (!showOtherOpenOrder || !imageReady) {
            setOtherOrderStickerVisible(false);
            return;
        }

        const frame = requestAnimationFrame(() => setOtherOrderStickerVisible(true));
        return () => cancelAnimationFrame(frame);
    }, [showOtherOpenOrder, imageReady, props.otherOpenOrderLabel]);

    const getPriceString = () => {
        const master = product;
        const currencyCode = master.currencyCode || user.user.currencyCode;
        const discount = props.discount || 0;
        const whp = master.whp || 0;
        const msrp = master.msrp || 0;
        const prefix = master.priceType === 'Diff' ? 'From ' : '';

        return prefix + (roundAmount(getWHPWithDiscount(whp, discount)) + (msrp !== 0 ? '/' + roundAmount(msrp) + ' ' + currencyCode : ''))
    }

    const mediaOverlayClass =
        (props.inBasket ? ' in-basket' : '') +
        (props.inBasket && stickerVisible ? ' in-basket--visible' : '') +
        (showOtherOpenOrder ? ' in-other-order' : '') +
        (showOtherOpenOrder && otherOrderStickerVisible ? ' in-other-order--visible' : '');

    return (
        <div className="pl-item pl-item--hover-b-image">
            <div
                className={'pl-item__media pl-item__media--hover-b' + mediaOverlayClass}
                data-other-order-label={showOtherOpenOrder ? props.otherOpenOrderLabel : undefined}
                title={showOtherOpenOrder ? props.otherOpenOrderTitle : undefined}
            >
                {
                    (product.b2BMasterComment || product.b2BColorComment) && <span className="pl-item__comment-icon"><FaComment /></span>
                }
                {props.onWishlistClick && (
                    <button
                        type="button"
                        className={'pl-item__wishlist' + (props.wishlistOn ? ' pl-item__wishlist--on' : '')}
                        disabled={props.wishlistLoading}
                        onClick={props.onWishlistClick}
                        aria-label={props.wishlistOn ? 'Remove from wishlist' : 'Add to wishlist'}
                    >
                        {props.wishlistOn ? <FaHeart /> : <FaRegHeart />}
                    </button>
                )}

                <div className="pl-item__image-stack">
                    <ProductListImage
                        src={imageUrl}
                        fallbackSrcs={imageFallbackSrcs}
                        alt={product.description || `${masterId} ${colorCode}`}
                        scrollPosition={props.scrollPosition}
                        onLoad={() => setImageReady(true)}
                    />
                    {hoverImageAvailable && (
                        <img
                            className="pl-item__hover-image"
                            src={hoverImageUrl}
                            alt=""
                            aria-hidden="true"
                            decoding="async"
                        />
                    )}
                </div>
            </div>

            <p className="pl-item__text">
                <span className="--highlighted">{ product.description }</span> { product.colorCode && product.colorName }
            </p>
            <p className="pl-item__text">
            { product.masterId } { product.colorCode }
            </p>
            {!!!hidePrice && <p className="pl-item__text --italic">
                { getPriceString() }
            </p>}
        </div>
    )
}


export default ProductListItem;
